import React from 'react'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Autocomplete, Box, ListItem, ListItemText, TextField } from '@mui/material'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'

import { FolderOutput } from '@core/api/generated'

const styles = {
  inputV2: {
    backgroundColor: (theme) => theme.palette.color.N010,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: '1px solid #e1e3e8',
      },
      '&:hover fieldset': {
        border: '1px solid #e1e3e8',
      },
      '&.Mui-focused fieldset': {
        border: '1px solid #e1e3e8',
      },
    },
  },
  autocompleteV2: {
    '& .MuiAutocomplete-paper': {
      maxHeight: 40,
    },
  },
}
type FoldersTreeAutocompleteProps = {
  isLoading?: boolean
  folders: FolderOutput[]
  value: FolderOutput | null
  onChange: (folder: FolderOutput | null) => void
  label?: string
  currentFolderId?: string | null
  mode: 'save' | 'move'
}

const searchOptions = {
  requireMatchAll: true,
  insideWords: true,
  findAllOccurrences: true,
}

const FoldersTreeAutocomplete = (props: FoldersTreeAutocompleteProps) => {
  const [inputValue, setInputValue] = React.useState('')

  return (
    <Autocomplete
      loading={props.isLoading}
      inputValue={inputValue}
      filterOptions={(options) => {
        if (!inputValue.trim()) {
          return options
        }

        const matchingIds = new Set()

        options.forEach((option) => {
          const isMatch = match(option.name, inputValue, searchOptions).length > 0

          if (isMatch) {
            option.breadcrumbs.forEach((breadcrumb) => {
              matchingIds.add(breadcrumb.id)
            })
          }
        })

        const filtered = options.filter((option) => {
          if (matchingIds.has(option.id)) return true

          return false
        })

        return filtered
      }}
      getOptionDisabled={(option) => {
        if (props.mode === 'move' && option.id === props.currentFolderId) {
          return true
        }

        return !option.permissions?.write
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
      }}
      options={props.folders}
      size="small"
      value={props.value || null}
      onChange={(e, value) => props.onChange(value || null)}
      fullWidth
      noOptionsText="No folders"
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label || 'Select a folder'}
          fullWidth
          sx={styles.inputV2}
          placeholder="Type to search"
        />
      )}
      getOptionLabel={(option) => {
        return option.name
      }}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderOption={(optionProps, option) => {
        const matches = match(option.name, inputValue, searchOptions)
        const parts: { highlight: boolean; text: string }[] = parse(option.name, matches)

        let secondaryText = ''

        if (props.mode === 'move' && option.id === props.currentFolderId) {
          secondaryText = 'You cannot move an item to the same folder'
        } else if (!option.permissions?.write) {
          secondaryText = 'You do not have access to this folder'
        }

        return (
          <ListItem {...optionProps} key={option.id} dense aria-label={option.name}>
            <ListItemText
              id={option.id}
              color="text.primary"
              secondaryTypographyProps={{ color: 'primary.main' }}
              sx={{ pl: (option.breadcrumbs.length - 1) * 2 }}
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  {option.breadcrumbs.length > 1 && <KeyboardArrowUpIcon sx={{ color: '' }} />}

                  <span>
                    {parts.map((part, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: part.highlight ? 700 : 400,
                        }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </span>
                </Box>
              }
              secondary={secondaryText}
            />
          </ListItem>
        )
      }}
    />
  )
}

export default FoldersTreeAutocomplete
